import { MoreHorizontal, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Thumb } from "@/components/Thumb";
import { STATUS_META } from "@/components/StatusBadge";
import { splitMeta, formatWhen, STATUS_DOT } from "@/lib/format";

const StatusLine = ({ status }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 text-[11px] font-medium",
      status === "pendiente" && "text-amber-700 dark:text-amber-400",
      status === "importante" && "text-teal-700 dark:text-teal-400",
      status === "aplicado" && "text-green-700 dark:text-green-400",
      (status === "inbox" || status === "archivado") && "text-muted-foreground",
    )}
  >
    <span className={cn("w-1.5 h-1.5 rounded-full", STATUS_DOT[status])} />
    {STATUS_META[status]?.label}
  </span>
);

// Card de rejilla (rediseño 2026-07-02). featured = card "Destacado" más grande.
export const ItemCard = ({ item, selected, onClick, featured }) => {
  const { duration, author } = splitMeta(item.meta);
  const summary =
    item.analyzeStatus === "enriqueciendo"
      ? "El agente está preparando el resumen…"
      : item.analyzeStatus === "error"
        ? "Guardado sin analizar todavía."
        : item.summary;

  if (featured) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.()}
        data-testid={`item-card-${item.id}`}
        className={cn(
          "sm:col-span-2 text-left rounded-lg border bg-card overflow-hidden cursor-pointer transition-all hover:border-teal-300/70 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          selected ? "border-primary/50 ring-1 ring-primary/30" : "border-border",
        )}
      >
        <div className="p-4 pb-3">
          <span className="rounded-full border bg-secondary px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground">
            Destacado
          </span>
        </div>
        <div className="px-4 pb-4 flex gap-4">
          <Thumb platform={item.platform} size="hero" className="w-44 shrink-0 aspect-[3/4] rounded-lg" />
          <div className="min-w-0 flex-1">
            <p className="font-serif text-xl leading-snug">{item.title}</p>
            <p className="mt-1 text-[13px] text-muted-foreground truncate">
              {author || item.platform} · {item.platform} · {duration}
            </p>
            <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{summary}</p>
            <div className="mt-3 flex gap-1.5 flex-wrap">
              {item.tags.slice(0, 3).map((t) => (
                <span key={t} className="rounded-md bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="px-4 py-2.5 border-t flex items-center justify-between">
          <StatusLine status={item.status} />
          <span className="flex items-center gap-2 text-xs text-muted-foreground tabular-nums">
            {formatWhen(item.createdAt)}
            <Bookmark className="h-3.5 w-3.5" strokeWidth={1.75} />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.()}
      data-testid={`item-card-${item.id}`}
      className={cn(
        "text-left rounded-lg border bg-card overflow-hidden cursor-pointer transition-all hover:border-teal-300/70 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring flex flex-col",
        selected ? "border-primary/50 ring-1 ring-primary/30" : "border-border",
      )}
    >
      <div className="relative">
        <Thumb platform={item.platform} size="card" />
        <button
          type="button"
          aria-label="Más acciones"
          onClick={(e) => {
            e.stopPropagation();
            toast("Más acciones en la versión final.");
          }}
          className="absolute top-2 right-2 w-7 h-7 rounded-md bg-background/90 border grid place-items-center text-muted-foreground hover:text-foreground"
        >
          <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </div>
      <div className="p-3 space-y-2 flex-1">
        <p className="font-medium text-[15px] leading-snug line-clamp-2">{item.title}</p>
        <p className="text-[13px] text-muted-foreground truncate">
          {author || item.mainTopic || "—"} · {item.platform}
          {duration ? ` · ${duration}` : ""}
        </p>
        <div className="flex gap-1.5 flex-wrap">
          {item.tags.slice(0, 3).map((t) => (
            <span key={t} className="rounded-md bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">
              {t}
            </span>
          ))}
        </div>
      </div>
      <div className="px-3 py-2 border-t flex items-center justify-between mt-auto">
        <span className="text-xs text-muted-foreground tabular-nums">{formatWhen(item.createdAt)}</span>
        <StatusLine status={item.status} />
      </div>
    </div>
  );
};
